<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { turnOffAutoComplete } from '$lib/utils/autoComplete';
    import { cn } from '$lib/utils/cn';

    import uniqueId from 'lodash/uniqueId';
    import { afterUpdate, onDestroy, onMount } from 'svelte';
    import { writable } from 'svelte/store';
    import clickOutside from 'svelte-outside-click';

    import DropdownCountriesButtons from './DropdownInput/DropdownCountriesButtons.svelte';
    import { createInputToDropdownKeyHandler } from './DropdownInput/dropdownKeyboardNavigation';
    import StatusIcon from './StatusIcon.svelte';
    import Dropdown from '../../_wrappers/Dropdown.svelte';
    import Emoji from '../../_wrappers/Emoji.svelte';

    import type { FormInputBinding } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';

    export let placeholder = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let finalFormInput: FormInputBinding;
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';
    export let showEmoji = false;
    export let emoji = '';
    export let language: Language = 'en';
    // Property introduced for Storybook to control the initial open state of the dropdown
    export let initialOpen = false;

    let showError = invalid && touched;
    let inputContainerClasses: string;
    let inputClasses: string;
    let countryDropdownOpen = initialOpen;
    let inputFocus = true;
    let value = finalFormInput?.value || '';
    let valueFromEvent = writable('');
    let registeredEventListeners: { event: string; handler: (e: Event) => void }[] = [];
    let inputElement: HTMLInputElement | undefined;

    const dropdownId = uniqueId('dropdown');

    afterUpdate(() => {
        showError = invalid && touched;
    });

    onMount(() => {
        ['street', 'postal', 'city', 'fullAddress'].forEach((interestedOn) => {
            const eventName = `${interestedOn}:selected`;
            const handler = (e: Event) => {
                const ev = e as CustomEvent<{ country: string }>;

                if (!ev.detail) {
                    return;
                }
                valueFromEvent.set(ev.detail.country);
            };
            document.addEventListener(eventName, handler);
            registeredEventListeners.push({ event: eventName, handler });
        });
    });

    onDestroy(() => {
        registeredEventListeners.forEach(({ event, handler }) => {
            document.removeEventListener(event, handler);
        });
    });

    $: {
        if ($valueFromEvent !== '' && value === '') {
            value = $valueFromEvent;
            finalFormInput.onChange(value);
        }
    }

    const closeDropdown = () => {
        countryDropdownOpen = false;
    };

    const openDropdown = () => {
        countryDropdownOpen = !countryDropdownOpen;
    };

    const handleClickOutside = () => {
        if (!countryDropdownOpen || inputFocus) return;

        closeDropdown();
    };

    const handleSelectCountry = (country: string) => {
        value = country;
        finalFormInput.onChange(value);

        closeDropdown();
    };

    const handleBlur = (event: FocusEvent) => {
        finalFormInput.onBlur(event);
        inputFocus = false;
    };

    const handleFocus = (event: FocusEvent) => {
        inputFocus = true;
        finalFormInput.onFocus(event);
        openDropdown();
    };

    const handleInput = (event: Event) => {
        const target = event.target as HTMLInputElement;
        value = target.value;
        finalFormInput.onChange(value);
    };

    $: handleInputKeyDown = createInputToDropdownKeyHandler(
        dropdownId,
        countryDropdownOpen,
        closeDropdown,
    );

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: inputContainerClasses = cn(
        borderRadius,
        'fun-box-border fun-w-full fun-border fun-bg-white fun-pl-4 fun-pr-6 fun-align-middle fun-ring-2 fun-ring-transparent fun-transition-all',
        {
            ['fun-pl-12']: showEmoji,
            ['fun-h-20']: invalid && showError,
            ['fun-h-14']: !invalid || !showError,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: active,
        },
    );

    $: inputClasses = cn(
        'fun-w-full fun-appearance-none fun-rounded-none fun-border-none fun-bg-transparent fun-align-middle fun-font-ui fun-text-base fun-font-light fun-placeholder-gray-500 fun-outline-none fun-transition-all',
        {
            'fun-h-full': !invalid || !showError,
            'fun-h-14': invalid && showError,
            'fun-pr-24': !required && pristine,
            'fun-pr-12': invalid && showError,
            'fun-pr-10': (!invalid || !showError) && (required || !pristine),
        },
    );
</script>

<div class="fun-relative fun-box-border fun-flex fun-items-center">
    {#if showEmoji}
        <div
            class="fun-absolute fun-left-4 fun-top-0 fun-flex fun-h-14 fun-select-none fun-items-center"
        >
            <u class="fun-no-underline" aria-hidden="true">
                <Emoji {emoji} size="20px" />
            </u>
        </div>
    {/if}

    {#if !required && !invalid && pristine}
        <u
            class="fun-absolute fun-right-6 fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
            >(optional)</u
        >
    {/if}

    <StatusIcon {showError} />

    <div
        class={inputContainerClasses}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
    >
        <input
            bind:this={inputElement}
            class={inputClasses}
            type="text"
            {...turnOffAutoComplete()}
            {placeholder}
            name={finalFormInput.name}
            value={finalFormInput.value}
            on:blur={handleBlur}
            on:focus={handleFocus}
            on:input={handleInput}
            on:keydown={handleInputKeyDown}
        />

        {#if errorMessage && showError}
            <div class="fun--mt-2.5 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600">
                {errorMessage}
            </div>
        {/if}
    </div>

    <div use:clickOutside={handleClickOutside}>
        <Dropdown
            id={dropdownId}
            open={countryDropdownOpen}
            contentClass="fun-w-full fun-box-border"
        >
            <DropdownCountriesButtons
                filter={value}
                open={countryDropdownOpen}
                handleSelect={handleSelectCountry}
                {language}
                {inputElement}
            />
        </Dropdown>
    </div>
</div>
